import ParentBadge from "../ParentBadge";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { snapBookingStart } from "./snapBookingStart";
import { loadPatientName } from "../names";
import { appointmentHasLinkedEncounter } from "../appointments/appointmentEncounter";
import { subjectPatientRef } from "../appointments/statusLabels";

export default function ProviderDayBoard({ date: controlledDate, onDateChange, schedules, names, events, timeZone, timeZoneControl, onDatesSet, onEventClick, onAppointmentMove, categories = [], availabilityWindows = [], slotMinutes = 20, columnOrder, onColumnOrderChange }) {
  const [localDate, setLocalDate] = useState(moment.tz(timeZone).format("YYYY-MM-DD"));
  const date = controlledDate ?? localDate;
  const setDate = onDateChange || setLocalDate;
  const [localOrder, setLocalOrder] = useState([]);
  const order = columnOrder ?? localOrder;
  const setOrder = onColumnOrderChange || setLocalOrder;
  const [draggedRef, setDraggedRef] = useState(null);
  const [dropRef, setDropRef] = useState(null);
  const columnPointer = useRef(null);
  const [announcement, setAnnouncement] = useState("");
  const [appointmentDrag, setAppointmentDrag] = useState(null);
  const [preview, setPreview] = useState(null);
  const appointmentPointer = useRef(null);
  const suppressClick = useRef(false);
  const clearMove = () => { appointmentPointer.current = null; setAppointmentDrag(null); setPreview(null); requestAnimationFrame(() => { suppressClick.current = false; }); };
  const scheduleScope = JSON.stringify(schedules.map(({ scheduleId, practitionerRef }) => [scheduleId, practitionerRef]).sort());
  useEffect(() => { appointmentPointer.current = null; setAppointmentDrag(null); setPreview(null); }, [date, timeZone, slotMinutes, scheduleScope]);
  const scrollRef = useRef(null);
  const [patientNames, setPatientNames] = useState({});
  const patientRefsKey = JSON.stringify([...new Set(events.filter((event) => event.extendedProps.kind === "appointment")
    .map((event) => subjectPatientRef(event.extendedProps.source)).filter(Boolean))].sort());
  useEffect(() => {
    let active = true;
    setPatientNames({});
    (async () => {
      const refs = JSON.parse(patientRefsKey);
      for (let offset = 0; offset < refs.length; offset += 10) {
        const entries = await Promise.all(refs.slice(offset, offset + 10).map(async (ref) => {
          const name = await loadPatientName(ref);
          return [ref, name && name !== ref && name !== "Unknown" ? name : "Patient name unavailable"];
        }));
        if (!active) return;
        setPatientNames((current) => ({ ...current, ...Object.fromEntries(entries) }));
      }
    })();
    return () => { active = false; };
  }, [patientRefsKey]);
  const people = [...new Map(schedules.map((schedule) => [schedule.practitionerRef, schedule])).values()];
  const rank = new Map(order.map((ref, index) => [ref, index]));
  const visible = [...people].sort((a, b) => (rank.get(a.practitionerRef) ?? Infinity) - (rank.get(b.practitionerRef) ?? Infinity));
  const movePractitioner = (from, to) => {
    const refs = visible.map((person) => person.practitionerRef);
    const source = refs.indexOf(from);
    const target = refs.indexOf(to);
    if (source < 0 || target < 0 || source === target) return;
    refs.splice(source, 1);
    refs.splice(target, 0, from);
    // Keep hidden practitioners in their positions when filters change.
    setOrder((previous) => {
      const remaining = [...refs];
      return [...previous.map((ref) => refs.includes(ref) ? remaining.shift() : ref), ...remaining];
    });
    setAnnouncement(`${names[from] || "Practitioner"} moved to position ${target + 1} of ${refs.length}.`);
  };
  const start = moment.tz(date, timeZone).startOf("day");
  const end = start.clone().add(1, "day");
  useEffect(() => {
    onDatesSet?.({ start: moment.tz(date, timeZone).startOf("day").toDate(), end: moment.tz(date, timeZone).startOf("day").add(1, "day").toDate() });
    // Only date and zone change the query range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, timeZone]);
  const dayEvents = events.filter((event) => moment(event.start).isBefore(end) && moment(event.end).isAfter(start));
  const startMinute = (event) => Math.max(0, moment(event.start).diff(start, "minutes"));
  const endMinute = (event) => Math.min(end.diff(start, "minutes"), moment(event.end).diff(start, "minutes"));
  const total = end.diff(start, "minutes");
  const rowHeight = 28;
  const height = (total / slotMinutes) * rowHeight;
  const moveRange = (from, to) => `${moment(from).tz(timeZone).format("h:mm A")}–${moment(to).tz(timeZone).format("h:mm A z")}`;
  const canMove = (event) => {
    const source = event.extendedProps.source;
    return !!onAppointmentMove && event.extendedProps.kind === "appointment" && source.status === "booked" &&
      !appointmentHasLinkedEncounter(source) &&
      source.practitionerRefs?.length === 1 && source.scheduleIds?.length === 1 &&
      moment(event.start).isAfter(moment()) && moment(event.end).isAfter(event.start) && moment(event.end).diff(moment(event.start), "minutes") <= total;
  };
  const columnTarget = (e) => document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-practitioner-column]")?.dataset.practitionerColumn;
  const moveColumnPointer = (e) => {
    const drag = columnPointer.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (!drag.moving && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 5) return;
    drag.moving = true;
    setDraggedRef(drag.ref);
    const target = columnTarget(e);
    setDropRef(target || null);
    const bounds = scrollRef.current?.getBoundingClientRect();
    if (bounds && e.clientX > bounds.right - 70) scrollRef.current.scrollLeft += 25;
    if (bounds && e.clientX < bounds.left + 100) scrollRef.current.scrollLeft -= 25;
  };
  const finishColumnPointer = (e, cancelled = false) => {
    const drag = columnPointer.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.moving && !cancelled) movePractitioner(drag.ref, columnTarget(e));
    columnPointer.current = null;
    setDraggedRef(null);
    setDropRef(null);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const bookingIncrement = categories.find((category) => category.serviceCategoryId === appointmentDrag?.event.extendedProps.source.serviceCategoryId)?.bookingIncrementMinutes;
  const targetAt = (requestedMinute, practitionerRef, event = appointmentDrag.event) => {
    const duration = moment(event.end).diff(moment(event.start));
    const requestedStart = start.valueOf() + requestedMinute * 60000;
    const category = categories.find((item) => item.serviceCategoryId === event.extendedProps.source.serviceCategoryId);
    const snapped = snapBookingStart({ requestedStart, duration, increment: category?.bookingIncrementMinutes * 60000,
      windows: availabilityWindows, scheduleIds: schedules.filter((schedule) => schedule.practitionerRef === practitionerRef).map((schedule) => schedule.scheduleId),
      serviceCategoryId: event.extendedProps.source.serviceCategoryId });
    const from = moment(snapped ?? requestedStart);
    return { practitionerRef, valid: snapped !== null, minute: from.diff(start, "minutes", true), duration: duration / 60000,
      startsAt: from.toISOString(), endsAt: from.clone().add(duration, "milliseconds").toISOString() };
  };
  const destination = (e, practitionerRef, drag = appointmentDrag) => {
    const duration = moment(drag.event.end).diff(moment(drag.event.start), "minutes", true);
    const offset = (e.clientY - e.currentTarget.getBoundingClientRect().top) / rowHeight * slotMinutes - drag.offset;
    return targetAt(Math.max(0, Math.min(total - duration, offset)), practitionerRef, drag.event);
  };
  const beginMove = (event, practitionerRef, offset = 0, keyboard = false) => {
    suppressClick.current = true;
    setAppointmentDrag({ event, practitionerRef, offset, keyboard });
    setAnnouncement(`Moving appointment from ${names[practitionerRef] || "Practitioner"}, ${moveRange(event.start, event.end)}.`);
  };
  const finishMove = (target, drag = appointmentDrag) => {
    const source = drag.event.extendedProps.source;
    if (target.valid && moment(target.startsAt).isAfter(moment()) && (target.practitionerRef !== drag.practitionerRef || !moment(target.startsAt).isSame(source.startsAt))) {
      onAppointmentMove({ ...target, appointment: source, fromPractitionerRef: drag.practitionerRef,
        patientName: patientNames[subjectPatientRef(source)] || "Patient name unavailable" });
    }
    clearMove();
  };
  const pointerDestination = (e, drag) => {
    const day = document.elementFromPoint(e.clientX, e.clientY)?.closest(".provider-board-day");
    if (!day || !scrollRef.current?.contains(day)) return null;
    const ref = day.closest("[data-practitioner-column]")?.dataset.practitionerColumn;
    return ref ? destination({ clientY: e.clientY, currentTarget: day }, ref, drag) : null;
  };
  const moveAppointmentPointer = (e) => {
    const drag = appointmentPointer.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (!drag.moving && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 5) return;
    if (!drag.moving) { drag.moving = true; beginMove(drag.event, drag.practitionerRef, drag.offset); }
    e.preventDefault();
    setPreview(pointerDestination(e, drag));
    const bounds = scrollRef.current.getBoundingClientRect();
    if (e.clientY > bounds.bottom - 55) scrollRef.current.scrollTop += 20;
    if (e.clientY < bounds.top + 100) scrollRef.current.scrollTop -= 20;
    if (e.clientX > bounds.right - 60) scrollRef.current.scrollLeft += 25;
    if (e.clientX < bounds.left + 95) scrollRef.current.scrollLeft -= 25;
  };
  const endAppointmentPointer = (e, cancelled = false) => {
    const drag = appointmentPointer.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const target = drag.moving && !cancelled ? pointerDestination(e, drag) : null;
    if (target) finishMove(target, drag);
    else clearMove();
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const eventsByPractitioner = useMemo(() => {
    const owners = new Map(schedules.map((schedule) => [schedule.scheduleId, schedule.practitionerRef]));
    const grouped = new Map();
    events.forEach((event) => {
      const source = event.extendedProps.source;
      const refs = new Set([source.practitionerRef, ...(source.practitionerRefs || []),
        owners.get(source.scheduleId), ...(source.scheduleIds || []).map((id) => owners.get(id))].filter(Boolean));
      refs.forEach((ref) => {
        if (!grouped.has(ref)) grouped.set(ref, []);
        grouped.get(ref).push(event);
      });
    });
    return grouped;
  }, [events, schedules]);
  const dayEventSet = new Set(dayEvents);
  const visibleDayEvents = [...new Set(visible.flatMap((person) => eventsByPractitioner.get(person.practitionerRef) || []))].filter((event) => dayEventSet.has(event));
  const scrollMinute = visibleDayEvents.length ? Math.min(...visibleDayEvents.map(startMinute)) : 0;
  const visiblePeopleKey = visible.map((person) => person.practitionerRef).sort().join(",");
  useEffect(() => {
    if (appointmentDrag?.keyboard && preview) scrollRef.current?.querySelector(".provider-board-move-preview")?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [appointmentDrag, preview]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollMinute / slotMinutes * rowHeight;
  }, [date, timeZone, visiblePeopleKey, scrollMinute, slotMinutes]);
  return <div className="provider-board">
    <div className="provider-board-toolbar">
      {timeZoneControl && React.cloneElement(timeZoneControl, { date })}
      <div className="schedule-inline-actions"><button type="button" aria-label="Previous day" onClick={() => setDate(start.clone().subtract(1, "day").format("YYYY-MM-DD"))}>‹</button>
        <input type="date" aria-label="Overview date" required value={date} onChange={(e) => { if (e.target.value) setDate(e.target.value); }} />
        <button type="button" aria-label="Next day" onClick={() => setDate(start.clone().add(1, "day").format("YYYY-MM-DD"))}>›</button>
        <button type="button" onClick={() => setDate(moment.tz(timeZone).format("YYYY-MM-DD"))}>Today</button></div>
      <div className="schedule-inline-actions"><span>{people.length} {people.length === 1 ? "practitioner" : "practitioners"}</span></div>
    </div>
    <span className="visually-hidden" role="status">{announcement}</span>
    {onAppointmentMove && <div className={`provider-board-move-hint${appointmentDrag ? " is-moving" : ""}`} aria-live="polite">
      {appointmentDrag ? <><strong>From {names[appointmentDrag.practitionerRef] || "Practitioner"} · {moveRange(appointmentDrag.event.start, appointmentDrag.event.end)}</strong>
        <span>{preview ? `To ${names[preview.practitionerRef] || "Practitioner"} · ${moveRange(preview.startsAt, preview.endsAt)} · ${appointmentDrag.keyboard ? "Arrows to move · Enter to review · Esc to cancel" : "Release to check availability"}` : "Move to a practitioner and time. Esc to cancel."}</span></> :
        <span>Drag to reschedule.</span>}
    </div>}
    {!visible.length ? <p className="scheduling-empty">No practitioners match these filters.</p> : <div className="provider-board-scroll" ref={scrollRef}
      tabIndex={0} role="region" aria-label="Practitioner schedule, scroll horizontally to see more practitioners">
      <div className="provider-board-columns" style={{ gridTemplateColumns: `65px repeat(${visible.length}, minmax(210px, 1fr))` }}>
        <div className="provider-board-time"><div className="provider-board-heading">{start.format("ddd D")}</div>
          <div style={{ height, position: "relative" }}>{Array.from({ length: Math.ceil(total / 60) }, (_, index) =>
            <span key={index} style={{ position: "absolute", top: (index * 60 / total) * height }}>{start.clone().add(index * 60, "minutes").format("h A")}</span>)}</div></div>
        {visible.map((person, index) => <div key={person.practitionerRef} data-practitioner-column={person.practitionerRef} className={`provider-board-column ${dropRef === person.practitionerRef ? "drop-target" : ""}`}>
          <div className="provider-board-heading provider-board-person">
            <button type="button" className="provider-board-drag" draggable={false} style={{ touchAction: "none", cursor: draggedRef === person.practitionerRef ? "grabbing" : "grab" }}
              aria-label={`Reorder ${names[person.practitionerRef] || "Practitioner"}`}
              title="Drag to reorder, or use Left and Right arrow keys"
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                e.currentTarget.focus({ preventScroll: true });
                columnPointer.current = { ref: person.practitionerRef, pointerId: e.pointerId, x: e.clientX, y: e.clientY, moving: false };
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={moveColumnPointer}
              onPointerUp={(e) => finishColumnPointer(e)}
              onPointerCancel={(e) => finishColumnPointer(e, true)}
              onLostPointerCapture={(e) => finishColumnPointer(e, true)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && columnPointer.current) {
                  const pointerId = columnPointer.current.pointerId;
                  columnPointer.current = null;
                  setDraggedRef(null); setDropRef(null);
                  if (e.currentTarget.hasPointerCapture?.(pointerId)) e.currentTarget.releasePointerCapture(pointerId);
                  e.preventDefault();
                  return;
                }
                if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
                e.preventDefault();
                const target = visible[index + (e.key === "ArrowLeft" ? -1 : 1)];
                if (target) movePractitioner(person.practitionerRef, target.practitionerRef);
                const handle = e.currentTarget;
                requestAnimationFrame(() => handle.scrollIntoView?.({ block: "nearest", inline: "nearest" }));
              }}>⠿</button>
            <button type="button" className="provider-board-name" title={names[person.practitionerRef]}
              onClick={() => onEventClick(null, { source: person, practitionerRef: person.practitionerRef })}>
              {names[person.practitionerRef] || "Practitioner"}
              <ParentBadge supplier={person.supplyOnly} organizationName={person.organizationName} />
            </button>
          </div>
          <div className={`provider-board-day${preview?.practitionerRef === person.practitionerRef ? " appointment-drop-target" : ""}`} style={{ height, backgroundSize: `100% ${rowHeight}px` }}
            >
            {preview?.practitionerRef === person.practitionerRef && <div className="provider-board-move-preview" style={{ top: `${preview.minute / total * 100}%`, height: `${preview.duration / total * 100}%` }}>
              <span>{moveRange(preview.startsAt, preview.endsAt)}{!preview.valid ? " · No valid start here" : ""}</span>
            </div>}
            {(eventsByPractitioner.get(person.practitionerRef) || []).filter((event) => dayEventSet.has(event)).map((event) => {
              const kind = event.extendedProps.kind;
              const rangeLabel = `${moment(event.start).tz(timeZone).format("h:mm A")}–${moment(event.end).tz(timeZone).format("h:mm A")}`;
              const compact = (endMinute(event) - startMinute(event)) / slotMinutes * rowHeight < 55;
              if (kind === "available") {
                return <div key={event.id} className={`provider-board-event available ${compact ? "compact" : ""}`} style={{
                  top: `${startMinute(event) / total * 100}%`, height: `${(endMinute(event) - startMinute(event)) / total * 100}%`,
                }} title={`Available · ${rangeLabel}`}>
                  {compact ? <span>{moment(event.start).tz(timeZone).format("h:mm")} · Available</span> : <>
                    <strong>Available</strong><span>{rangeLabel}</span>
                  </>}
                </div>;
              }
              const status = event.extendedProps.source.status;
              const patientRef = subjectPatientRef(event.extendedProps.source);
              const cardTitle = kind === "appointment"
                ? (patientNames[patientRef] || (patientRef ? "Loading patient…" : "Patient name unavailable"))
                : event.title;
              const label = kind === "availability" ? "Working hours" : kind === "block" ? "Blocked time" :
                ({ cancelled: "Cancelled", fulfilled: "Completed", no_show: "No show", proposed: "Pending" }[status] || "");
              return <button type="button" key={event.id} className={`provider-board-event ${kind} ${compact ? "compact" : ""}${appointmentDrag?.event.id === event.id ? " is-dragging" : ""}`} data-status={status} style={{
                top: `${startMinute(event) / total * 100}%`, height: `${(endMinute(event) - startMinute(event)) / total * 100}%`,
                touchAction: canMove(event) ? "none" : undefined,
                cursor: canMove(event) ? appointmentDrag?.event.id === event.id ? "grabbing" : "grab" : undefined,
              }} draggable={false}
                onPointerDown={(e) => {
                  if (e.button !== 0 || !canMove(event)) return;
                  e.preventDefault();
                  e.currentTarget.focus({ preventScroll: true });
                  appointmentPointer.current = { event, practitionerRef: person.practitionerRef, pointerId: e.pointerId,
                    x: e.clientX, y: e.clientY, moving: false,
                    offset: (e.clientY - e.currentTarget.getBoundingClientRect().top) / rowHeight * slotMinutes };
                  e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onPointerMove={moveAppointmentPointer}
                onPointerUp={(e) => endAppointmentPointer(e)}
                onPointerCancel={(e) => endAppointmentPointer(e, true)}
                onLostPointerCapture={(e) => endAppointmentPointer(e, true)}
                onKeyDown={(e) => {
                  if (!canMove(event)) return;
                  if (e.key === "Escape") { e.preventDefault(); clearMove(); setAnnouncement("Move cancelled."); return; }
                  if (!appointmentDrag && e.key === " ") {
                    e.preventDefault(); beginMove(event, person.practitionerRef, 0, true);
                    setPreview(targetAt(startMinute(event), person.practitionerRef, event));
                    return;
                  }
                  if (appointmentDrag?.event.id !== event.id || !preview) return;
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); finishMove(preview); return; }
                  if (!e.key.startsWith("Arrow")) return;
                  e.preventDefault();
                  const nextIndex = visible.findIndex((p) => p.practitionerRef === preview.practitionerRef) + (e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0);
                  const ref = visible[nextIndex]?.practitionerRef || preview.practitionerRef;
                  const nextMinute = preview.minute + (e.key === "ArrowUp" ? -bookingIncrement : e.key === "ArrowDown" ? bookingIncrement : 0);
                  if (!Number.isFinite(nextMinute)) return;
                  const target = targetAt(Math.max(0, Math.min(total - preview.duration, nextMinute)), ref);
                  setPreview(target);
                  const from = moment(target.startsAt);
                  setAnnouncement(`${names[ref] || "Practitioner"}, ${moveRange(from, from.clone().add(preview.duration, "minutes"))}. Enter to review, Escape to cancel.`);
                }}
                onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } onEventClick(event, { ...event.extendedProps, practitionerRef: person.practitionerRef }); }}
                title={`${label ? `${label} · ` : ""}${cardTitle} · ${moment(event.start).tz(timeZone).format("h:mm A")}–${moment(event.end).tz(timeZone).format("h:mm A")}`}>
                {compact ? <span>{moment(event.start).tz(timeZone).format("h:mm")} · {kind === "appointment" ? cardTitle : label}</span> : <>
                  <strong>{kind === "appointment" ? cardTitle : label}</strong><span>{moment(event.start).tz(timeZone).format("h:mm A")}–{moment(event.end).tz(timeZone).format("h:mm A")}</span>
                  {kind === "appointment" ? (label && <span>{label}</span>) : <span>{cardTitle}</span>}
                </>}
              </button>;
            })}
          </div>
        </div>)}
      </div>
    </div>}
  </div>;
}
